import * as React from "react";
import { TerminalTheme } from "@/src/types";
import { cn } from "@/src/lib/utils";

/**
 * FreshnessBadge — surfaces the SSE connection state in the topbar.
 *   live         → emerald, "Live"
 *   reconnecting → amber, "Reconnecting…"
 *   stale        → rose, "Stale"
 * Fed by the AppShell's EventSource state (it owns the connections).
 * Theme-aware: uses the active TerminalTheme's neutral text for the label,
 * with fixed status colors so the signal reads the same in every theme.
 */
export type ConnState = "live" | "reconnecting" | "stale";

const MAP: Record<ConnState, { color: string; label: string }> = {
  live: { color: "#10b981", label: "Live" },
  reconnecting: { color: "#f59e0b", label: "Reconnecting…" },
  stale: { color: "#f43f5e", label: "Stale" },
};

export interface FreshnessBadgeProps {
  state: ConnState;
  theme?: TerminalTheme;
  className?: string;
}

export const FreshnessBadge: React.FC<FreshnessBadgeProps> = ({
  state,
  theme,
  className,
}) => {
  const { color, label } = MAP[state];
  return (
    <span
      title={`SSE: ${label}`}
      style={{
        color,
        backgroundColor: color + "14",
        borderColor: color + "44",
      }}
      className={cn(
        "inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        className
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          state !== "live" && "animate-pulse"
        )}
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
};
